import React, { useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { Download } from 'react-bootstrap-icons';
import { useSearchParams } from 'react-router-dom';

import { PageTitle } from '../../components/PageTitle';
import { RefreshButton } from '../../components/RefreshButton';
import { CurrencyRecord, SupportedCurrency } from '../../domain/currency';
import { CombinedSettingsTypes } from '../../domain/settings';
import { buildTransportOrdersFilters } from '../../domain/statsFilters';
import { useCurrencyRepository } from '../../repository/currency/useCurrencyRepository';
import { useSettingsRepository } from '../../repository/settings/useSettingsRepository';
import { useStatsRepository } from '../../repository/stats/useStatsRepository';
import { useTransportOrdersRepository } from '../../repository/transport-orders/useTransportOrdersRepository';
import { ContractorsStatsForMonth } from './components/specific-stats/ContractorsStatsForMonth';
import { CreatorsStatsForMonth } from './components/specific-stats/CreatorsStatsForMonth';
import { CumulativeProfitStats } from './components/specific-stats/cumulative-and-daily-profit/CumulativeProfitStats';
import { TransportOrdersByStatusStats } from './components/specific-stats/TransportOrdersByStatusStats';
import { TransportOrdersStatsByMonth } from './components/specific-stats/TransportOrdersStatsForMonth';
import { buildStatsFiltersFromSearchParams } from './components/stats-filters/buildStatsFiltersFromSearchParams';
import { StatsFilters } from './components/stats-filters/StatsFilters';
import { StatsQuickFilters } from './components/stats-filters/StatsQuickFilters';
import { StatsLoadingSkeleton } from './StatsLoadingSkeleton';
import { ExportStatsModal } from './components/export/ExportStatsModal';

export const StatsWrapper = () => {
    const { data: currencyRecord, refetch: refetchCurrency, isLoading: isLoadingCurrency, isRefetching: isRefetchingCurrency } = useCurrencyRepository();
    const { data: settings, refetch: refetchSettings, isLoading: isSettingsLoading, isRefetching: isRefetchingSettings } = useSettingsRepository();

    if (!currencyRecord || !settings) {
        return <StatsLoadingSkeleton />;
    }

    const handleRefresh = async () => {
        await Promise.all([
            refetchCurrency(),
            refetchSettings(),
        ]);
    };

    const isRefreshing = isLoadingCurrency || isRefetchingCurrency ||
        isSettingsLoading || isRefetchingSettings;

    return <Stats
        currencyRecord={currencyRecord}
        settings={settings}
        isRefreshing={isRefreshing}
        refetch={handleRefresh}
    />;
};

type StatsProps = {
    currencyRecord: CurrencyRecord;
    settings: CombinedSettingsTypes;
    isRefreshing: boolean;
    refetch: () => void;
}

const Stats = (props: StatsProps) => {
    const { currencyRecord, settings, refetch, isRefreshing: isCurrencyRecordAndSettingsRefreshing } = props;

    const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(settings.otherSettings.defaultCurrency);
    const [searchParams] = useSearchParams();
    const [showExportModal, setShowExportModal] = useState(false);

    const filters = buildStatsFiltersFromSearchParams(searchParams);

    const { data: transportOrders, refetch: refetchTransportOrders, isLoading: isLoadingTransportOrders, isRefetching: isRefetchingTransportOrders } = useTransportOrdersRepository(buildTransportOrdersFilters(filters));
    const { data: stats, refetch: refetchStats, isLoading: isLoadingStats, isRefetching: isRefetchingStats } = useStatsRepository(filters, selectedCurrency);

    const [windowDimensions, setWindowDimensions] = React.useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    const handleRefresh = async () => {
        await Promise.all([
            refetchTransportOrders(),
            refetchStats(),
            refetch(),
        ]);
    };

    React.useEffect(() => {
        const handleResize = () => {
            setWindowDimensions({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (!currencyRecord || !transportOrders || !stats || !settings) {
        return <StatsLoadingSkeleton />;
    }

    const isRefreshing = isCurrencyRecordAndSettingsRefreshing ||
        isLoadingTransportOrders || isRefetchingTransportOrders ||
        isLoadingStats || isRefetchingStats;

    return <>
        <div className="d-flex align-items-center">
            <PageTitle className="mb-0">Statystyki</PageTitle>
            <RefreshButton
                className="ms-2"
                isRefreshing={isRefreshing}
                refreshFunction={handleRefresh}
            />
        </div>
        <StatsFilters className="mb-3" stats={stats} settings={settings} />
        <div className="d-flex align-items-center justify-content-between">
            <StatsQuickFilters filters={filters} />
            <Button
                variant="primary"
                onClick={() => setShowExportModal(true)}
            >
                <Download className="me-2" />
                Eksportuj
            </Button>
        </div>
        <div className="d-flex flex-row mb-3">
            <Card className="me-2">
                <Card.Body>
                    <TransportOrdersByStatusStats transportOrdersStatusesStats={stats.transportOrdersStatusStats} />
                </Card.Body>
            </Card>
            <Card className="flex-grow-1 ms-2">
                <Card.Body>
                    <CumulativeProfitStats
                        key={windowDimensions.width}
                        stats={stats}
                        filters={filters}
                        currencyRecord={currencyRecord}
                        selectedCurrency={selectedCurrency}
                        onCurrencyChange={setSelectedCurrency}
                    />
                </Card.Body>
            </Card>
        </div>
        <div className="d-flex">
            <div className="flex-grow-1 me-3 d-flex flex-column">
                <Card className="mb-3">
                    <Card.Body>
                        <CreatorsStatsForMonth stats={stats} forCurrency="EUR" />
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <ContractorsStatsForMonth
                            stats={stats}
                            forCurrency="EUR"
                        />
                    </Card.Body>
                </Card>
            </div>

            <Card className="flex-grow-3">
                <Card.Body>
                    <TransportOrdersStatsByMonth filters={filters} currencyRecord={currencyRecord} />
                </Card.Body>
            </Card>
        </div>

        <ExportStatsModal
            stats={stats}
            show={showExportModal}
            onHide={() => setShowExportModal(false)}
            filters={filters}
            selectedCurrency={selectedCurrency}
        />
    </>;
};
